import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Stack,
  LinearProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import api from '../api/axios';
import AssessmentPayloadEditor from './AssessmentPayloadEditor';
import {
  getAllTaskSpecs,
  getExpectedIacsForQualification
} from '../utils/taskAssessmentIacAudit';

const STEPS = ['Type', 'Sources', 'Parameters', 'Review'];

function getApiBase() {
  return (api.defaults.baseURL || '').replace(/\/$/, '');
}

async function postProgramExamGenerateStream(qualificationId, body, onEvent) {
  const token = localStorage.getItem('token');
  const url = `${getApiBase()}/qualifications/${qualificationId}/learning-content/task-assessments/generate-stream`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && token !== 'undefined' ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  const reader = res.body && res.body.getReader();
  if (!reader) {
    throw new Error('No response body');
  }
  const dec = new TextDecoder();
  let buffer = '';
  let last = null;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += dec.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const block of parts) {
      const line = block.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      try {
        const data = JSON.parse(line.slice(6));
        last = data;
        if (onEvent) onEvent(data);
      } catch (_) {}
    }
  }
  return last;
}

export default function ProgramExamWizardModal({
  open,
  onClose,
  qualificationId,
  qualification,
  assignedItems,
  onSaved
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [kind, setKind] = useState(null);
  const [bokSources, setBokSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [selectedTaskCodes, setSelectedTaskCodes] = useState(() => new Set());
  const [selectedIacCodes, setSelectedIacCodes] = useState(() => new Set());
  const [questionCount, setQuestionCount] = useState(50);
  const [totalMarks, setTotalMarks] = useState(50);
  const [optionCount, setOptionCount] = useState(4);
  const [generating, setGenerating] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [error, setError] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [payload, setPayload] = useState(null);
  const [saving, setSaving] = useState(false);
  const [enforceIacCoverage, setEnforceIacCoverage] = useState(false);
  const [iacCoverageWarningCodes, setIacCoverageWarningCodes] = useState(null);
  const wizardSessionRef = useRef(null);

  const allTasks = useMemo(() => getAllTaskSpecs(assignedItems || {}), [assignedItems]);
  const taskTitleByCode = useMemo(() => {
    const m = new Map();
    allTasks.forEach((t) => m.set(t.code, t.title));
    return m;
  }, [allTasks]);

  const allIacs = useMemo(
    () => getExpectedIacsForQualification(assignedItems || {}),
    [assignedItems]
  );

  const curriculumTaskCodes = useMemo(() => new Set(allTasks.map((t) => t.code)), [allTasks]);

  const mergedBokRows = useMemo(() => {
    return (bokSources || []).map((s) => ({
      code: s.code,
      title: taskTitleByCode.get(s.code) || s.title || s.code,
      has_openai_file: s.has_openai_file,
      inCurriculum: curriculumTaskCodes.has(s.code)
    }));
  }, [bokSources, taskTitleByCode, curriculumTaskCodes]);

  const loadBokSources = useCallback(async () => {
    if (!qualificationId) return;
    setLoadingSources(true);
    try {
      const res = await api.get(
        `/qualifications/${qualificationId}/learning-content/bok-assessment-task-sources`
      );
      setBokSources(res.data?.sources || []);
    } catch (_) {
      setBokSources([]);
    } finally {
      setLoadingSources(false);
    }
  }, [qualificationId]);

  useEffect(() => {
    if (!open) {
      wizardSessionRef.current = null;
      return;
    }
    const key = 'program';
    if (wizardSessionRef.current === key) return;
    wizardSessionRef.current = key;
    setActiveStep(0);
    setKind(null);
    setSelectedTaskCodes(new Set());
    setSelectedIacCodes(new Set());
    setQuestionCount(50);
    setTotalMarks(50);
    setOptionCount(4);
    setGenerating(false);
    setProgressMsg('');
    setError(null);
    setAssessmentId(null);
    setPayload(null);
    setSaving(false);
    setEnforceIacCoverage(false);
    setIacCoverageWarningCodes(null);
    setBokSources([]);
  }, [open]);

  useEffect(() => {
    if (!open || activeStep !== 1) return;
    loadBokSources();
  }, [open, activeStep, loadBokSources]);

  useEffect(() => {
    if (!open) return;
    if (kind === 'free_text') {
      setTotalMarks((m) => (m === 50 || m === 25 ? 60 : m));
      setQuestionCount((q) => (q === 50 || q === 25 ? 6 : q));
    } else if (kind === 'mcq') {
      setQuestionCount(50);
      setTotalMarks(50);
      setOptionCount(4);
    }
  }, [kind, open]);

  const programTitle =
    qualification?.name ||
    qualification?.title ||
    qualification?.programme_name ||
    'Program';

  const bokCodes = useMemo(() => mergedBokRows.map((row) => row.code), [mergedBokRows]);
  const iacCodes = useMemo(() => allIacs.map((i) => i.code), [allIacs]);
  const allBokSelected = bokCodes.length > 0 && bokCodes.every((code) => selectedTaskCodes.has(code));
  const someBokSelected = bokCodes.some((code) => selectedTaskCodes.has(code));
  const allIacSelected = iacCodes.length > 0 && iacCodes.every((code) => selectedIacCodes.has(code));
  const someIacSelected = iacCodes.some((code) => selectedIacCodes.has(code));

  const toggleTask = (code) => {
    setSelectedTaskCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const toggleAllTasks = () => {
    setSelectedTaskCodes(allBokSelected ? new Set() : new Set(bokCodes));
  };

  const toggleIac = (code) => {
    setSelectedIacCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const toggleAllIacs = () => {
    setSelectedIacCodes(allIacSelected ? new Set() : new Set(iacCodes));
  };

  const selectAssessmentKind = (k) => {
    setError(null);
    setKind(k);
    setActiveStep(1);
  };

  const moduleTaskSpecs = useMemo(() => {
    const specs = [];
    for (const code of selectedTaskCodes) {
      specs.push({
        code,
        title: taskTitleByCode.get(code) || code
      });
    }
    return specs;
  }, [selectedTaskCodes, taskTitleByCode]);

  const iacsPayload = useMemo(() => {
    const list = [];
    for (const i of allIacs) {
      if (selectedIacCodes.has(i.code)) {
        list.push({ code: i.code, title: i.title || i.code });
      }
    }
    return list;
  }, [allIacs, selectedIacCodes]);

  const runGenerate = async () => {
    setError(null);
    if (moduleTaskSpecs.length === 0) {
      setError('Select at least one task with BoK content.');
      return;
    }
    if (iacsPayload.length === 0) {
      setError('Select at least one required IAC.');
      return;
    }
    setGenerating(true);
    setProgressMsg('Starting…');
    try {
      const last = await postProgramExamGenerateStream(
        qualificationId,
        {
          scope: 'program',
          kind,
          program_title: programTitle,
          module_task_specs: moduleTaskSpecs,
          iacs: iacsPayload,
          question_count: questionCount,
          total_marks: totalMarks,
          option_count: optionCount,
          allow_repeat_iac: true,
          enforce_iac_coverage: enforceIacCoverage
        },
        (ev) => {
          if (ev.type === 'status' && ev.message) setProgressMsg(ev.message);
          if (ev.type === 'error') setError(ev.message || 'Generation failed');
        }
      );
      if (last && last.type === 'result' && last.assessment) {
        setAssessmentId(last.assessment.id);
        setPayload(last.assessment.payload);
        const missing = last.warnings && Array.isArray(last.warnings.missing_iac_codes)
          ? last.warnings.missing_iac_codes
          : null;
        setIacCoverageWarningCodes(missing && missing.length ? missing : null);
        setActiveStep(3);
      } else if (last && last.type === 'error') {
        setError(last.message || 'Generation failed');
      } else {
        setError('Unexpected response from server');
      }
    } catch (e) {
      setError(e.message || 'Generation failed');
    } finally {
      setGenerating(false);
      setProgressMsg('');
    }
  };

  const savePayload = async (status) => {
    if (!assessmentId || !payload) return;
    setSaving(true);
    setError(null);
    try {
      await api.put(
        `/qualifications/${qualificationId}/learning-content/task-assessments/${assessmentId}`,
        { payload, status: status || 'saved' }
      );
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  const canProceedFromSources = selectedTaskCodes.size > 0 && selectedIacCodes.size > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Program exam — {programTitle}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Choose BoK sources (tasks with saved content) and required IACs. Generation uses the same
          vector-store and file_search flow as module exams, scoped to the tasks and IACs you select.
        </Typography>
        <Stepper activeStep={activeStep} sx={{ py: 2 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <Stack spacing={2} sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Choose a format for the program-level exam.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
              <Button variant="contained" size="large" onClick={() => selectAssessmentKind('mcq')}>
                Multiple choice (MCQ)
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => selectAssessmentKind('free_text')}
              >
                Free text
              </Button>
            </Stack>
          </Stack>
        )}

        {activeStep === 1 && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {loadingSources ? (
              <LinearProgress />
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="subtitle2">BoK sources (tasks)</Typography>
                  {mergedBokRows.length > 0 && (
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={allBokSelected}
                          indeterminate={someBokSelected && !allBokSelected}
                          onChange={toggleAllTasks}
                        />
                      }
                      label="Select all"
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Prefer tasks with &quot;AI file ready&quot; (sync via View PDF on each task). Tasks
                  without a synced file may be skipped during generation.
                </Typography>
                <Box sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid #e2e8f0', p: 1, borderRadius: 1 }}>
                  <FormGroup>
                    {mergedBokRows.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No BoK documents found for this qualification yet.
                      </Typography>
                    ) : (
                      mergedBokRows.map((row) => (
                        <FormControlLabel
                          key={row.code}
                          control={
                            <Checkbox
                              checked={selectedTaskCodes.has(row.code)}
                              onChange={() => toggleTask(row.code)}
                            />
                          }
                          label={
                            <span>
                              <strong>{row.code}</strong> — {row.title}
                              {row.has_openai_file ? (
                                <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                                  AI file ready
                                </Typography>
                              ) : (
                                <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                                  Sync PDF needed
                                </Typography>
                              )}
                              {!row.inCurriculum && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  (outside current ELO assignments)
                                </Typography>
                              )}
                            </span>
                          }
                        />
                      ))
                    )}
                  </FormGroup>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mt: 1
                  }}
                >
                  <Typography variant="subtitle2">IACs to cover</Typography>
                  {allIacs.length > 0 && (
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={allIacSelected}
                          indeterminate={someIacSelected && !allIacSelected}
                          onChange={toggleAllIacs}
                        />
                      }
                      label="Select all"
                    />
                  )}
                </Box>
                <Box sx={{ maxHeight: 180, overflow: 'auto', border: '1px solid #e2e8f0', p: 1, borderRadius: 1 }}>
                  <FormGroup>
                    {allIacs.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No IAC subtasks found in assigned curriculum. Complete ELO assignments in Step 3.
                      </Typography>
                    ) : (
                      allIacs.map((i) => (
                        <FormControlLabel
                          key={i.code}
                          control={
                            <Checkbox
                              checked={selectedIacCodes.has(i.code)}
                              onChange={() => toggleIac(i.code)}
                            />
                          }
                          label={
                            <span>
                              <strong>{i.code}</strong> — {i.title}{' '}
                              <Typography component="span" variant="caption" color="text.secondary">
                                ({i.task_code})
                              </Typography>
                            </span>
                          }
                        />
                      ))
                    )}
                  </FormGroup>
                </Box>
              </>
            )}
          </Stack>
        )}

        {activeStep === 2 && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Number of questions"
              type="number"
              value={questionCount}
              onChange={(e) => setQuestionCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
              inputProps={{ min: 1, max: 100 }}
            />
            <TextField
              label="Total marks"
              type="number"
              value={totalMarks}
              onChange={(e) => setTotalMarks(Math.max(1, parseInt(e.target.value, 10) || 1))}
              inputProps={{ min: 1, max: 1000 }}
            />
            {kind === 'mcq' && (
              <TextField
                label="Options per question"
                type="number"
                value={optionCount}
                onChange={(e) =>
                  setOptionCount(Math.min(8, Math.max(2, parseInt(e.target.value, 10) || 4)))
                }
                inputProps={{ min: 2, max: 8 }}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={enforceIacCoverage}
                  onChange={(e) => setEnforceIacCoverage(e.target.checked)}
                />
              }
              label="Require every selected IAC (generation fails if any are missing). Leave this off to continue and review the gaps."
            />
            {generating && (
              <Box>
                <LinearProgress />
                <Typography variant="caption" sx={{ mt: 1 }} display="block">
                  {progressMsg || 'Working…'}
                </Typography>
              </Box>
            )}
          </Stack>
        )}

        {activeStep === 3 && payload && (
          <>
            {iacCoverageWarningCodes && iacCoverageWarningCodes.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                These IACs are not tagged on any question yet: {iacCoverageWarningCodes.join(', ')}. You can
                still edit and save this exam.
              </Alert>
            )}
            <AssessmentPayloadEditor kind={kind} payload={payload} onChange={setPayload} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {activeStep === 1 && (
          <Button onClick={() => setActiveStep(0)} disabled={generating}>
            Back
          </Button>
        )}
        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={() => setActiveStep(2)}
            disabled={!canProceedFromSources || loadingSources}
          >
            Next
          </Button>
        )}
        {activeStep === 2 && (
          <Button onClick={() => setActiveStep(1)} disabled={generating}>
            Back
          </Button>
        )}
        {activeStep === 2 && (
          <Button variant="contained" onClick={runGenerate} disabled={generating}>
            {generating ? 'Generating…' : 'Generate'}
          </Button>
        )}
        {activeStep === 3 && (
          <>
            <Button variant="outlined" disabled={saving} onClick={() => savePayload('saved')}>
              Save
            </Button>
            <Button variant="contained" disabled={saving} onClick={() => savePayload('approved')}>
              Save and approve
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
